import { useMemo } from 'react';
import { DateTime } from 'luxon';

export const SYSTEM_TIME_ZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

export type TimezoneOption = {
  code: string;
  id: string;
  label: string;
};

/** Maps a timezone value to its corresponding code (e.g. America/Los_Angeles => PT) */
export const getTimezoneValueToCodeMapper = (
  timezones: TimezoneOption[],
): Record<string, string> =>
  Object.fromEntries((timezones ?? []).map((tz) => [tz.id, tz.code]));

/** Maps a timezone label to its corresponding code (e.g. Pacific Time => PT) */
export const getTimezoneLabelToCodeMapper = (
  timezones: TimezoneOption[],
): Record<string, string> =>
  Object.fromEntries((timezones ?? []).map((tz) => [tz.label, tz.code]));

/** Maps a timezone code to its corresponding value (e.g. PT => Pacific Time) */
export const getTimezoneCodeToValueMapper = (
  timezones: TimezoneOption[],
): Record<string, string> =>
  Object.fromEntries((timezones ?? []).map((tz) => [tz.code, tz.id]));

export function useTimezoneLabelToCodeMapper(timezones: TimezoneOption[]) {
  return useMemo<Record<string, string>>(
    () => getTimezoneLabelToCodeMapper(timezones),
    [timezones],
  );
}

/**
 * Converts IANA timezone format to abbreviation (e.g., "America/Los_Angeles" => "PST" or "PDT")
 * Automatically accounts for daylight saving time by using the specified reference date.
 *
 * @param ianaTimezone - IANA timezone string (e.g., "America/Los_Angeles", "America/New_York")
 * @param referenceDate - Optional ISO date string to determine DST status (defaults to current date)
 * @returns Timezone abbreviation (e.g., "PST", "PDT", "EST", "EDT") or original value if invalid
 *
 * @example
 * getTimezoneAbbreviation('America/Los_Angeles') // => "PST" (winter) or "PDT" (summer)
 * getTimezoneAbbreviation('America/Los_Angeles', '2024-01-15') // => "PST" (winter date)
 * getTimezoneAbbreviation('America/Los_Angeles', '2024-07-15') // => "PDT" (summer date)
 */
export const getTimezoneAbbreviation = (
  ianaTimezone: string,
  referenceDate?: string,
): string => {
  if (!ianaTimezone) return '';

  try {
    const base = referenceDate
      ? DateTime.fromISO(referenceDate, { zone: ianaTimezone })
      : DateTime.local().setZone(ianaTimezone);

    if (!base.isValid) return ianaTimezone;

    const abbreviation = base.toFormat('ZZZZ');
    if (abbreviation === 'Invalid DateTime' || abbreviation === ianaTimezone) {
      return ianaTimezone;
    }
    return abbreviation;
  } catch {
    return ianaTimezone;
  }
};

/**
 * Gets the browser's timezone in IANA format (e.g., "America/Chicago").
 * This is safe to send to the backend as it always returns IANA format,
 * avoiding issues with Windows timezone formats like "Central Standard Time".
 */
export const getBrowserTimezone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    // Fallback to UTC if browser doesn't support timezone detection
    return 'UTC';
  }
};

/**
 * Normalizes a timezone string to IANA format.
 * If the input is already IANA format, returns it unchanged.
 * If the input is Windows format or invalid, returns the browser's timezone.
 *
 * @param timezone - The timezone string to normalize (may be IANA, Windows format, or null/undefined)
 * @returns IANA format timezone string
 */
export const normalizeTimezone = (
  timezone: string | null | undefined,
): string => {
  if (!timezone) {
    return getBrowserTimezone();
  }

  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return timezone;
  } catch {
    return getBrowserTimezone();
  }
};

type TimezoneEnumeration = {
  code: string;
  id: string;
  label?: string;
  externalId?: string;
};

export const getDefaultTimezoneIANA = (
  timezonesResponse?: TimezoneEnumeration[],
): string => {
  return (
    timezonesResponse?.find((tz) => tz.id === SYSTEM_TIME_ZONE)?.id ||
    SYSTEM_TIME_ZONE
  );
};

export const convertToLocalTime = (
  datetimeString: string,
  timeZone: string,
) => {
  try {
    // Check if datetimeString is valid
    if (!datetimeString || typeof datetimeString !== 'string') {
      return '';
    }

    const date = new Date(datetimeString);

    // Check if the date is invalid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Check if timeZone is valid
    if (!timeZone || typeof timeZone !== 'string') {
      return '';
    }

    // Get the time in the specified timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      timeZone,
      year: 'numeric',
    });

    const parts = formatter.formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    const hour = parts.find((part) => part.type === 'hour')?.value;
    const minute = parts.find((part) => part.type === 'minute')?.value;
    const second = parts.find((part) => part.type === 'second')?.value;

    if (!year || !month || !day || !hour || !minute || !second) {
      return '';
    }

    // Create ISO string representing the local time
    const localTimeString = `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`;

    // Validate the result
    const resultDate = new Date(localTimeString);
    if (isNaN(resultDate.getTime())) {
      return '';
    }

    return localTimeString;
  } catch (error) {
    // Return empty string for any errors (e.g., invalid timezone)
    console.warn('convertToLocalTime error:', error);
    return '';
  }
};
